// The admin sidebar: each built-in role's menu, and the Access Control module each
// page belongs to. Shared by the Sidebar and Access Control, where the superadmin
// hides / shows pages per role and per administrator.

// How a sidebar saved before pages were hidden one by one is read: hiding a module hid
// each page listed with it here. (What each module's permission covers is listed in
// Access Control — not always the same pages.)
export const PATH_TO_MODULE = {
    '/dashboard':        'dashboard',
    '/users':            'users',
    '/admins':           'admins',
    '/branches':         'branches',
    '/nas':              'routers',
    '/plans':            'plans',
    '/vouchers':         'vouchers',
    '/media':            'media',
    '/sessions':         'sessions',
    '/session-report':   'sessions',
    '/devices':          'devices',
    '/device-status':    'devices',
    '/templates':        'templates',
    '/campaigns':        'campaigns',
    '/reports':          'reports',
    '/auth-sets':        'auth_sets',
    '/integrations':     'integrations',
    '/billing':          'billing',
    '/webhooks':         'webhooks',
    '/connected-users':  'sessions',
    '/scheduler-jobs':   'plans',
    '/traffic':          'reports',
    '/upgrade-approvals':'admins',
    '/vlan-profiles':    'routers',
    '/policy-rules':     'settings',
    '/membership-tiers': 'plans',
    '/addons':           'plans',
    '/plan-templates':   'plans',
    '/user-types':       'users',
    '/rooms':            'branches',
    '/branch-pages':     'branches',
    '/landing-pages':    'landing_pages',
    '/dot-logs':         'audit_logs',
    '/logs':             'audit_logs',
    '/guest-activity':   'audit_logs',
    '/pms-logs':         'integrations',
    '/payment-config':   'billing',
    '/access-control':   'settings',
    '/syslog':           'audit_logs',
    '/activity-log':     'audit_logs',
    '/setup-guide':      'dashboard',
    '/device-auth':      'sessions',
};

export const MENU_BY_ROLE = {
    superadmin: [
        { path: '/dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { path: '/setup-guide', label: 'Setup Guide', icon: 'fa-rocket' },
        { type: 'divider', label: 'Organization' },
        { path: '/tenants', label: 'Tenants', icon: 'fa-building' },
        { path: '/branches', label: 'Branches', icon: 'fa-code-branch' },
        { path: '/admins', label: 'Administrators', icon: 'fa-user-shield' },
        // Superadmin oversees every tenant (often mixed types) and defaults
        // to "All Branches" with nothing selected, so there's no single
        // branch to gate on — always show it, same as Tenants/Branches above.
        { type: 'divider', label: 'Hotel Management' }, { path: '/rooms', label: 'Room Management', icon: 'fa-hotel' }, { path: '/branch-pages', label: 'Branch Pages', icon: 'fa-th-large' },
        { type: 'divider', label: 'Network & Services' },
        { path: '/nas', label: 'NAS Devices', icon: 'fa-server' },
        { path: '/vlan-profiles', label: 'VLAN Profiles', icon: 'fa-network-wired' },
        { path: '/plans', label: 'Internet Plans', icon: 'fa-tags' },
        { path: '/auth-sets', label: 'Authorization Sets', icon: 'fa-shield-alt' },
        { path: '/user-types', label: 'User Types', icon: 'fa-users-cog' },
        { path: '/plan-templates', label: 'Plan Templates', icon: 'fa-layer-group' },
        { path: '/addons', label: 'Internet Add-Ons', icon: 'fa-shopping-cart' },
        { path: '/membership-tiers', label: 'Membership Tiers', icon: 'fa-medal' },
        { path: '/policy-rules', label: 'Policy Engine', icon: 'fa-sitemap' },
        { path: '/vouchers', label: 'Vouchers', icon: 'fa-ticket-alt' },
        { path: '/media', label: 'Media Library', icon: 'fa-photo-video' },
        { path: '/templates', label: 'Splash Pages', icon: 'fa-paint-brush' },
        { type: 'divider', label: 'Operations' },
        { path: '/devices', label: 'Connected Devices', icon: 'fa-laptop' },
        { path: '/device-status', label: 'Device Status', icon: 'fa-signal' },
        { path: '/sessions', label: 'Active Sessions', icon: 'fa-wifi' },
        { path: '/connected-users', label: 'Connected Users', icon: 'fa-broadcast-tower' },
        { path: '/device-auth', label: 'Device Authorization', icon: 'fa-shield-alt' },
        { path: '/campaigns', label: 'Marketing & Leads', icon: 'fa-bullhorn' },
        { path: '/scheduler-jobs', label: 'Plan Scheduler', icon: 'fa-calendar-alt' },
        { path: '/upgrade-approvals', label: 'Upgrade Approvals', icon: 'fa-check-double' },
        { type: 'divider', label: 'Integrations' },
        { path: '/integrations', label: 'PMS Integrations', icon: 'fa-plug' },
        // Every report and log in one place.
        { type: 'divider', label: 'Reports & Logs' },
        { path: '/reports', label: 'Reports', icon: 'fa-chart-bar' },
        { path: '/session-report', label: 'Session Dashboard', icon: 'fa-chart-line' },
        { path: '/traffic', label: 'Interface Traffic', icon: 'fa-chart-area' },
        { path: '/guest-activity', label: 'Guest Activity', icon: 'fa-user-clock' },
        { path: '/logs', label: 'Hotspot Logs', icon: 'fa-stream' },
        { path: '/auth-logs',    label: 'Authorization Logs', icon: 'fa-list-alt' },
        { path: '/radius-report', label: 'RADIUS Report', icon: 'fa-shield-alt' },
        { path: '/pms-logs',     label: 'PMS Activity Logs',  icon: 'fa-history' },
        { path: '/activity-log', label: 'Login & Activity', icon: 'fa-history' },
        { path: '/dot-logs', label: 'DoT Compliance Logs', icon: 'fa-shield-alt' },
        { path: '/syslog', label: 'Syslog Dashboard', icon: 'fa-network-wired' },
        { type: 'divider', label: 'Access & Security' },
        { path: '/access-control', label: 'Access Control', icon: 'fa-user-lock' },
        { path: '/vpn-users', label: 'VPN Users', icon: 'fa-user-secret' },
        { type: 'divider', label: 'Finance & System' },
        { path: '/billing', label: 'Billing & Subscriptions', icon: 'fa-credit-card' },
        { path: '/payment-config', label: 'Payment Gateways', icon: 'fa-money-check-alt' },
        { path: '/webhooks', label: 'Webhooks', icon: 'fa-satellite-dish' },
        { path: '/settings', label: 'Platform Settings', icon: 'fa-cog' },
    ],
    tenant_admin: [
        { path: '/dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { path: '/setup-guide', label: 'Setup Guide', icon: 'fa-rocket' },
        { type: 'divider', label: 'Organization' },
        { path: '/branches', label: 'Branches', icon: 'fa-code-branch' },
        { path: '/admins', label: 'Administrators', icon: 'fa-user-shield' },
        { type: 'divider', label: 'Hotel Management' }, { path: '/rooms', label: 'Room Management', icon: 'fa-hotel' }, { path: '/branch-pages', label: 'Branch Pages', icon: 'fa-th-large' },
        { type: 'divider', label: 'Network & Services' },
        { path: '/nas', label: 'NAS Devices', icon: 'fa-server' },
        { path: '/vlan-profiles', label: 'VLAN Profiles', icon: 'fa-network-wired' },
        { path: '/plans', label: 'Internet Plans', icon: 'fa-tags' },
        { path: '/user-types', label: 'User Types', icon: 'fa-users-cog' },
        { path: '/plan-templates', label: 'Plan Templates', icon: 'fa-layer-group' },
        { path: '/addons', label: 'Internet Add-Ons', icon: 'fa-shopping-cart' },
        { path: '/membership-tiers', label: 'Membership Tiers', icon: 'fa-medal' },
        { path: '/policy-rules', label: 'Policy Engine', icon: 'fa-sitemap' },
        { path: '/vouchers', label: 'Vouchers', icon: 'fa-ticket-alt' },
        { path: '/media', label: 'Media Library', icon: 'fa-photo-video' },
        { path: '/templates', label: 'Splash Pages', icon: 'fa-paint-brush' },
        { type: 'divider', label: 'Operations' },
        { path: '/devices', label: 'Connected Devices', icon: 'fa-laptop' },
        { path: '/device-status', label: 'Device Status', icon: 'fa-signal' },
        { path: '/sessions', label: 'Active Sessions', icon: 'fa-wifi' },
        { path: '/connected-users', label: 'Connected Users', icon: 'fa-broadcast-tower' },
        { path: '/device-auth', label: 'Device Authorization', icon: 'fa-shield-alt' },
        { path: '/campaigns', label: 'Marketing & Leads', icon: 'fa-bullhorn' },
        { path: '/scheduler-jobs', label: 'Plan Scheduler', icon: 'fa-calendar-alt' },
        { path: '/upgrade-approvals', label: 'Upgrade Approvals', icon: 'fa-check-double' },
        { type: 'divider', label: 'Integrations' },
        { path: '/integrations', label: 'PMS Integrations', icon: 'fa-plug' },
        // Every report and log in one place.
        { type: 'divider', label: 'Reports & Logs' },
        { path: '/reports', label: 'Reports', icon: 'fa-chart-bar' },
        { path: '/session-report', label: 'Session Dashboard', icon: 'fa-chart-line' },
        { path: '/traffic', label: 'Interface Traffic', icon: 'fa-chart-area' },
        { path: '/guest-activity', label: 'Guest Activity', icon: 'fa-user-clock' },
        { path: '/logs', label: 'Hotspot Logs', icon: 'fa-stream' },
        { path: '/auth-logs',    label: 'Authorization Logs', icon: 'fa-list-alt' },
        { path: '/radius-report', label: 'RADIUS Report', icon: 'fa-shield-alt' },
        { path: '/pms-logs',     label: 'PMS Activity Logs',  icon: 'fa-history' },
        { path: '/activity-log', label: 'Login & Activity', icon: 'fa-history' },
        { type: 'divider', label: 'Finance' },
        { path: '/payment-config', label: 'Payment Gateways', icon: 'fa-money-check-alt' },
        { path: '/webhooks', label: 'Webhooks', icon: 'fa-satellite-dish' },
        { path: '/auth-sets', label: 'Authorization Sets', icon: 'fa-shield-alt' },
        // { path: '/billing', label: 'Billing & Subscriptions', icon: 'fa-credit-card' },
    ],
    branch_admin: [
        { path: '/dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { path: '/setup-guide', label: 'Setup Guide', icon: 'fa-rocket' },
        { type: 'divider', label: 'Management' },
        { path: '/branches', label: 'My Branch', icon: 'fa-code-branch' },
        { path: '/admins', label: 'Staff', icon: 'fa-user-shield' },
        { type: 'divider', label: 'Hotel Management' }, { path: '/rooms', label: 'Room Management', icon: 'fa-hotel' }, { path: '/branch-pages', label: 'Branch Pages', icon: 'fa-th-large' },
        { type: 'divider', label: 'Services' },
        { path: '/nas', label: 'NAS Devices', icon: 'fa-server' },
        { path: '/vlan-profiles', label: 'VLAN Profiles', icon: 'fa-network-wired' },
        { path: '/plans', label: 'Internet Plans', icon: 'fa-tags' },
        { path: '/user-types', label: 'User Types', icon: 'fa-users-cog' },
        { path: '/addons', label: 'Internet Add-Ons', icon: 'fa-shopping-cart' },
        { path: '/vouchers', label: 'Vouchers', icon: 'fa-ticket-alt' },
        { path: '/media', label: 'Media Library', icon: 'fa-photo-video' },
        { path: '/templates', label: 'Splash Pages', icon: 'fa-paint-brush' },
        { type: 'divider', label: 'Operations' },
        { path: '/devices', label: 'Connected Devices', icon: 'fa-laptop' },
        { path: '/device-status', label: 'Device Status', icon: 'fa-signal' },
        { path: '/sessions', label: 'Active Sessions', icon: 'fa-wifi' },
        { path: '/connected-users', label: 'Connected Users', icon: 'fa-broadcast-tower' },
        { path: '/device-auth', label: 'Device Authorization', icon: 'fa-shield-alt' },
        { path: '/scheduler-jobs', label: 'Plan Scheduler', icon: 'fa-calendar-alt' },
        { path: '/upgrade-approvals', label: 'Upgrade Approvals', icon: 'fa-check-double' },
        // Every report and log in one place.
        { type: 'divider', label: 'Reports & Logs' },
        { path: '/reports', label: 'Reports', icon: 'fa-chart-bar' },
        { path: '/session-report', label: 'Session Dashboard', icon: 'fa-chart-line' },
        { path: '/traffic', label: 'Interface Traffic', icon: 'fa-chart-area' },
        { path: '/guest-activity', label: 'Guest Activity', icon: 'fa-user-clock' },
        { path: '/logs', label: 'Hotspot Logs', icon: 'fa-stream' },
        { path: '/activity-log', label: 'Login & Activity', icon: 'fa-history' },
    ],
    support: [
        { path: '/dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { type: 'divider', label: 'Live Operations' },
        { path: '/sessions', label: 'Active Sessions', icon: 'fa-wifi' },
        { path: '/connected-users', label: 'Connected Users', icon: 'fa-broadcast-tower' },
        { path: '/devices', label: 'Connected Devices', icon: 'fa-laptop' },
        { path: '/device-status', label: 'Device Status', icon: 'fa-signal' },
        { path: '/device-auth', label: 'Device Authorization', icon: 'fa-shield-alt' },
        { path: '/rooms', label: 'Room Management', icon: 'fa-hotel' },
        { type: 'divider', label: 'Support Tools' },
        { path: '/vouchers', label: 'Vouchers', icon: 'fa-ticket-alt' },
        { path: '/media', label: 'Media Library', icon: 'fa-photo-video' },
        { path: '/users', label: 'Users', icon: 'fa-users' },
        { type: 'divider', label: 'Reference' },
        { path: '/plans', label: 'Internet Plans', icon: 'fa-tags' },
        { path: '/nas', label: 'NAS Devices', icon: 'fa-server' },
        { type: 'divider', label: 'Reports & Logs' },
        { path: '/reports', label: 'Reports', icon: 'fa-chart-bar' },
        { path: '/session-report', label: 'Session Dashboard', icon: 'fa-chart-line' },
    ],
    viewer: [
        { path: '/dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt' },
        { type: 'divider', label: 'Statistics' },
        { path: '/sessions', label: 'Active Sessions', icon: 'fa-wifi' },
        { path: '/connected-users', label: 'Connected Users', icon: 'fa-broadcast-tower' },
        { path: '/rooms', label: 'Room Management', icon: 'fa-hotel' },
        { type: 'divider', label: 'Reference' },
        { path: '/devices', label: 'Connected Devices', icon: 'fa-laptop' },
        { path: '/device-status', label: 'Device Status', icon: 'fa-signal' },
        { path: '/plans', label: 'Internet Plans', icon: 'fa-tags' },
        { type: 'divider', label: 'Reports & Logs' },
        { path: '/reports', label: 'Reports', icon: 'fa-chart-bar' },
        { path: '/session-report', label: 'Session Dashboard', icon: 'fa-chart-line' },
    ]
};

// Pages that open inside another sidebar entry (the page builder lives under Splash Pages).
const PAGE_OF_PATH = { '/landing-pages': '/templates' };

/** The menu a role sees. A custom role sees that of the built-in role it is based on. */
export const menuFor = (role) => MENU_BY_ROLE[role] || MENU_BY_ROLE.viewer;

/** Every page of a role's menu, in menu order, with the section it sits in. */
export function pagesOf(role) {
    const pages = [];
    let section = '';
    for (const item of menuFor(role)) {
        if (item.type === 'divider') { section = item.label; continue; }
        pages.push({ ...item, section });
    }
    return pages;
}

/**
 * Whether a page is hidden for an administrator: their own setting first (always shown,
 * or hidden), else their role's — the page, or (as saved before the sidebar was set
 * page by page) its whole module. The dashboard is never hidden.
 * @param {{ roleHidden?: string[], userHidden?: string[], userShown?: string[] } | null} access
 */
export function pageHidden(path, access) {
    if (!access || !path || path === '/dashboard') return false;
    if (access.userShown?.includes(path)) return false;
    if (access.userHidden?.includes(path)) return true;
    return !!access.roleHidden?.includes(path) || !!access.roleHidden?.includes(PATH_TO_MODULE[path]);
}

/** The sidebar page a location belongs to ('/rooms/12' → '/rooms') in a role's menu, or null. */
export function pageOfLocation(pathname, role) {
    const inside = Object.entries(PAGE_OF_PATH).find(([p]) => pathname === p || pathname.startsWith(`${p}/`));
    const path = inside ? inside[1] : pathname;
    return pagesOf(role).map((p) => p.path)
        .filter((p) => path === p || path.startsWith(`${p}/`))
        .sort((a, b) => b.length - a.length)[0] || null;
}

/** A menu without hidden pages, and without the section headings left with nothing under them. */
export function visibleMenu(role, access) {
    const items = menuFor(role).filter((item) => item.type === 'divider' || !pageHidden(item.path, access));
    return items.filter((item, i) => item.type !== 'divider' || (items[i + 1] && items[i + 1].type !== 'divider'));
}
